import { useState, useEffect } from 'react'
import { formatDateTime } from '@shared/utils/datetime'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ViewColumn as ViewColumnIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material'
import { ticketService } from '../../../shared/services/TicketService'
import { Ticket, TicketStatus } from '../../../shared/types/entities'
import { OrganizationFilter } from './filters/OrganizationFilter'
import { TicketKanbanBoard } from './kanban/TicketKanbanBoard'
import { TicketDetailModal } from './ticket/TicketDetailModal'
import { CreateTicketModal } from './ticket/CreateTicketModal'

interface TicketListCardProps {
  organizationId?: string
  showCreateButton?: boolean
  onCreateClick?: () => void
  variant?: 'admin' | 'customer' | 'agent'
  showOrganizationFilter?: boolean
}

export function TicketListCard({
  organizationId,
  showCreateButton = false,
  onCreateClick,
  variant = 'admin',
  showOrganizationFilter = false
}: TicketListCardProps) {
  const navigate = useNavigate()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'All'>('All')
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [childSiteFilter, setChildSiteFilter] = useState('')
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [view, setView] = useState<'cards' | 'kanban'>(() => {
    const saved = localStorage.getItem('adminTicketsView')
    return (saved === 'kanban' || saved === 'cards') ? saved : 'cards'
  })

  const loadTickets = async () => {
    try {
      setLoading(true)
      setError(null)
      const filters = {
        status: statusFilter === 'All' ? undefined : statusFilter,
        search: searchQuery || undefined,
        organizationId: organizationId || organizationFilter || undefined,
        childSiteId: childSiteFilter || undefined
      }
      const response = await ticketService.getAllTickets(1, 50, filters)
      setTickets(response.data)
    } catch (err: any) {
      setError(err.message || 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTickets()
  }, [statusFilter, organizationId, organizationFilter, childSiteFilter])

  const handleSearch = () => {
    loadTickets()
  }

  const handleOrganizationChange = (orgId: string, siteId: string) => {
    setOrganizationFilter(orgId)
    setChildSiteFilter(siteId)
  }

  const toggleView = () => {
    const newView = view === 'cards' ? 'kanban' : 'cards'
    setView(newView)
    localStorage.setItem('adminTicketsView', newView)
  }

  const handleStatusChange = async (ticketId: string, newStatus: TicketStatus) => {
    try {
      await ticketService.updateTicketStatus(ticketId, newStatus)
      await loadTickets() // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to update ticket status')
      throw err // Re-throw so the component can handle it
    }
  }

  const getStatusColor = (status: TicketStatus): 'default' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'Open':
        return 'warning'
      case 'InProgress':
        return 'info'
      case 'OnHold':
        return 'warning'
      case 'WaitingOnThirdParty':
        return 'warning'
      case 'Resolved':
        return 'success'
      case 'Closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'Urgent':
        return '#d32f2f'
      case 'High':
        return '#f57c00'
      case 'Medium':
        return '#fbc02d'
      case 'Low':
        return '#388e3c'
      default:
        return '#757575'
    }
  }

  const formatDate = (dateString: string): string => {
    return formatDateTime(dateString, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleTicketClick = (ticket: Ticket) => {
    // Open ticket in modal (except for customer variant which needs different UI)
    if (variant === 'customer') {
      navigate(`/customer/tickets/${ticket.ticketId}`)
    } else {
      setSelectedTicketId(ticket.ticketId)
      setModalOpen(true)
    }
  }

  const handleCreateClick = () => {
    if (onCreateClick) {
      onCreateClick()
    } else {
      // Open create ticket modal
      setCreateModalOpen(true)
    }
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Tickets</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={view === 'cards' ? <ViewColumnIcon /> : <ViewListIcon />}
            onClick={toggleView}
          >
            {view === 'cards' ? 'Kanban' : 'Cards'}
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadTickets} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {showCreateButton && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            >
              Create Ticket
            </Button>
          )}
        </Box>
      </Box>

      <Box mb={3}>
        <Tabs
          value={statusFilter}
          onChange={(_, value) => setStatusFilter(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" value="All" />
          <Tab label="Open" value="Open" />
          <Tab label="In Progress" value="InProgress" />
          <Tab label="On Hold" value="OnHold" />
          <Tab label="Waiting on Third Party" value="WaitingOnThirdParty" />
          <Tab label="Resolved" value="Resolved" />
          <Tab label="Closed" value="Closed" />
        </Tabs>
      </Box>

      <Box mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Search tickets by subject, description, or ticket number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleSearch}>
                    Search
                  </Button>
                </InputAdornment>
              )
            }}
          />
          {showOrganizationFilter && (
            <OrganizationFilter
              value={childSiteFilter || organizationFilter}
              onChange={handleOrganizationChange}
            />
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {view === 'kanban' ? (
        <TicketKanbanBoard
          tickets={tickets}
          loading={loading}
          onTicketClick={(ticketId) => {
            const ticket = tickets.find(t => t.ticketId === ticketId)
            if (ticket) handleTicketClick(ticket)
          }}
          onStatusChange={handleStatusChange}
        />
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                No tickets found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {statusFilter === 'All'
                  ? "No tickets have been created yet."
                  : `No ${statusFilter.toLowerCase()} tickets found.`}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {tickets.map((ticket) => (
            <Card
              key={ticket.ticketId}
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 },
                borderLeft: `4px solid ${getPriorityColor(ticket.priority)}`
              }}
              onClick={() => handleTicketClick(ticket)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                      <Typography variant="caption" color="textSecondary">
                        {ticket.ticketNumber}
                      </Typography>
                      <Chip
                        label={ticket.status}
                        color={getStatusColor(ticket.status)}
                        size="small"
                      />
                      <Chip
                        label={ticket.priority}
                        size="small"
                        sx={{
                          bgcolor: getPriorityColor(ticket.priority),
                          color: 'white'
                        }}
                      />
                      {ticket.categoryName && (
                        <Chip
                          label={ticket.categoryName}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#9c27b0', color: '#9c27b0' }}
                        />
                      )}
                      {ticket.childSiteName && (
                        <Chip
                          label={ticket.childSiteName}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#4caf50', color: '#4caf50' }}
                        />
                      )}
                      {ticket.organizationName && !ticket.childSiteName && (
                        <Chip
                          label={ticket.organizationName}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#ff9800', color: '#ff9800' }}
                        />
                      )}
                      {ticket.assignedAgentName && (
                        <Chip
                          label={`Assigned: ${ticket.assignedAgentName}`}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#2196f3', color: '#2196f3' }}
                        />
                      )}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {ticket.subject}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {ticket.description}
                    </Typography>
                  </Box>
                  <Box ml={2} textAlign="right">
                    <Typography variant="caption" color="textSecondary" display="block">
                      Created
                    </Typography>
                    <Typography variant="body2">{formatDate(ticket.createdAt)}</Typography>
                    {ticket.dueDate && (
                      <>
                        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                          Due
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            new Date(ticket.dueDate) < new Date() && ticket.status !== 'Resolved'
                              ? 'error'
                              : 'textPrimary'
                          }
                        >
                          {formatDate(ticket.dueDate)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        open={modalOpen}
        ticketId={selectedTicketId}
        onClose={() => {
          setModalOpen(false)
          setSelectedTicketId(null)
        }}
        onUpdate={() => {
          // Refresh ticket list when ticket is updated
          loadTickets()
        }}
      />

      {/* Create Ticket Modal */}
      <CreateTicketModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(ticketId: string) => {
          // Refresh ticket list and optionally open the new ticket
          loadTickets()
          setSelectedTicketId(ticketId)
          setModalOpen(true)
        }}
        userType={variant === 'customer' ? 'customer' : 'agent'}
      />
    </Box>
  )
}
